import React, { useMemo } from "react";

import { connect, ConnectedProps } from "react-redux";
import { EditOutlined, BoxIcons, IconTypes } from "us.icons";
import { RootState } from "us.helper/types";
import * as Actions from "actions";
import {
  $Button,
  $Tooltip,
  $Typography,
  $Input,
  $Popover,
  $Tag,
  $InputNumber,
  $Divider,
  $Message,
} from "us.common/components";
import { useTranslation } from "react-i18next";
import { Formik } from "formik";
import { ColorResult, SketchPicker } from "react-color";
import {
  updateSectionByName,
  changeSize,
  changePosition,
  getMaxSizeForSection,
  getSectionDataByRule,
  changeSectionBgColor,
  validateSection,
  isDuplicateSectionName,
} from "components/TemplateEditor/Functions";
import { CommonProperties } from "../CommonProperties";
import { ISection, ISectionRule } from "interfaces";
import {
  displayRuleExpression,
  getSectionByName,
  getSectionRuleByRule,
  updateTemplateSectionRule,
} from "functions";
import { SectionValidationError } from "components/TemplateEditor/Interfaces";
const { Paragraph } = $Typography;

interface ISectionRules {
  isXml?: boolean;
  onAddNewRule: () => void;
}

/**
 * @description - Section selection rule component for view and add rules.
 * @link Design Document -
 * @author Ishan Udyoga <ishanud@unicorn-solutions.com>
 * @since 11/09/2022
 */
const SectionRules: React.FC<ISectionRules & PropsFromRedux> = (props) => {
  const {
    isXml,
    section,
    template,
    selectSection,
    onAddNewRule,
    updateTemplate,
  } = props;
  const { t } = useTranslation();
  const { width, height, x, y } = section.properties;
  const { templateContent } = template.data ?? {};

  const sectionRules = useMemo(() => {
    return section.rules?.filter(({ isDeleted }: any) => !isDeleted);
  }, [section, templateContent]);

  const validationErrors = useMemo(() => {
    return validateSection(section.id);
  }, [section, templateContent, sectionRules]);

  const mazSize = getMaxSizeForSection(section);
  const margins: { top: number; bottom: number; left: number; right: number } =
    templateContent?.margin ?? {
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
    };

  /**
   * @description - Handle rule selection
   * @param {string} selectedRule - Selected rule from the list
   */
  const onSelectRule = (selectedRule: string) => {
    sectionRules.map(({ rule }: any, _index: number) => {
      if (isXml) {
        selectSection &&
          selectSection({
            ...section,
            selectedRule,
          });
      } else {
        if (rule == selectedRule) {
          const selectedRuleHtml = document.querySelector(
            `div[sectionname='${section.name}'][rule='${rule}']`
          );
          selectedRuleHtml?.removeAttribute("hidden");
          selectedRuleHtml?.classList.add("active");
          const data = getSectionDataByRule(section.name, rule);
          selectSection &&
            selectSection({
              ...section,
              ...data,
            });
        } else {
          const otherRuleHtml = document.querySelector(
            `div[sectionname='${section.name}'][rule='${rule}']`
          );
          otherRuleHtml?.setAttribute("hidden", "true");
          otherRuleHtml?.classList.remove("active");
        }
      }
    });
  };

  /**
   * @description - Convert given pixel value to cm
   * @param {number} px - Pixel value
   * @returns {number} - Matching cm value FFFFFF
   */
  const toCentimeters = (px: number): number => {
    return px * 0.026458;
  };

  /**
   * @description - Convert given pixel value to cm
   * @param {number} cmValue - Pixel value
   * @returns {number} - Matching cm value FFFFFF
   */
  const toPixel = (cmValue: number): number => {
    return Math.round(cmValue / 0.026458);
  };

  const handleColorChange = (color?: ColorResult) => {
    try {
      const selectedSection = getSectionByName(
        section?.name,
        template.data.templateContent.pages
      );
      if (selectedSection) {
        const sectionRule = getSectionRuleByRule(
          section.selectedRule,
          selectedSection
        );
        const updatedContent = changeSectionBgColor(
          sectionRule?.content,
          color?.hex ?? "transparent"
        );
        const oldSectionRule = getSectionRuleByRule(
          section.selectedRule,
          selectedSection
        );
        if (oldSectionRule) {
          const updatedSectionRule: ISectionRule = {
            ...oldSectionRule,
            content: updatedContent ?? oldSectionRule.content,
          };
          const updatedTemplate = updateTemplateSectionRule(
            section.name,
            section.selectedRule,
            updatedSectionRule,
            template.data
          );
          updateTemplate && updateTemplate(updatedTemplate);
        }
      }

      selectSection &&
        selectSection({
          ...section,
          color: color?.hex ?? "transparent",
        });
    } catch (error) {
      console.error("handleColorChange failed with : ", error);
      $Message.error(
        t("US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.SECTION_COLOR_CHANGE_FAILED")
      );
    }
  };

  const handleSectionNameChange = (newName: string) => {
    if (newName && selectSection) {
      selectSection({
        ...section,
        name: newName,
      });
      // Get updated template
      const updatedTemplate = updateSectionByName(template.data, section.name, {
        name: newName,
      });
      updateTemplateData(updatedTemplate);
    }
  };

  const updateTemplateData = (updatedTemplate: any) => {
    // update template with changes
    updateTemplate &&
      updateTemplate({
        ...template.data,
        ...updatedTemplate,
      });
  };

  /**
   * @description handle width and height changes of a section
   * @param {number} value - value in cm
   * @param {"width" | "height"} propertyName - size change property
   */
  const handleSizeChange = (
    value: number,
    propertyName: "width" | "height"
  ) => {
    const updatedProperties = Object.assign(section.properties, {
      [propertyName]: toPixel(value),
    });
    selectSection &&
      selectSection({
        ...section,
        properties: updatedProperties,
      });

    changeSize(section.id, updatedProperties.width, updatedProperties.height);
  };

  /**
   * @description handle x and y transform of a section
   * @param {number} value - transform value in cm
   * @param {"x" | "y"} propertyName - transform axis
   */
  const handlePositionChange = (value: number, propertyName: "x" | "y") => {
    const updatedProperties = Object.assign(section.properties, {
      [propertyName]: section.properties[propertyName] + toPixel(value),
    });
    selectSection &&
      selectSection({
        ...section,
        properties: updatedProperties,
      });
    if (propertyName === "x") {
      changePosition(section.id, toPixel(value), 0);
    }
    if (propertyName === "y") {
      changePosition(section.id, 0, toPixel(value));
    }
  };

  const isSectionLocked = () => {
    try {
      if (template.data && template.data.templateContent) {
        const sectionToCheck = template.data.templateContent.pages.map(
          (page: { sections: any[] }) => {
            return page.sections.find(
              (sectionItem: ISection) => sectionItem.name == section.name
            );
          }
        );
        if (sectionToCheck && sectionToCheck.length > 0) {
          return sectionToCheck[0]?.locked;
        } else {
          return false;
        }
      }
    } catch (error) {
      return false;
    }
  };

  return (
    <Formik
      initialValues={{
        name: section.name ?? "",
        width: Number(toCentimeters(width)).toFixed(2),
        height: Number(toCentimeters(height)).toFixed(2),
        x: Number(toCentimeters(x)).toFixed(2),
        y: Number(toCentimeters(y)).toFixed(2),
      }}
      enableReinitialize
      validateOnChange
      validateOnBlur
      onSubmit={() => {}}
    >
      {({
        values,
        handleChange,
        handleBlur,
        handleSubmit,
        isSubmitting,
        isValidating,
        resetForm,
        isValid,
        errors,
        touched,
        setErrors,
        ...restProps
      }: any) => (
        <div>
          {section.name && (
            <>
              <div className="panel-title pr-1">
                <h4 className="d-flex align-items-center pl-2 py-2 m-0">
                  {t("COMMON.NAME")}
                </h4>
              </div>

              <div className="components-content p-2 mb-2">
                <$Input
                  name="name"
                  style={{ width: "100%" }}
                  size="small"
                  required
                  maxLength={250}
                  onChange={(e: any) => {
                    restProps.setFieldValue("name", e.target.value);
                    if (isDuplicateSectionName(template.data, e.target.value)) {
                      $Message.error(
                        `${t(
                          "US.USC.TEMPLATEEDITOR:VALIDATIONS.SECTION_NAME_CANNOT_BE_DUPLICATED"
                        )}`
                      );
                    } else {
                      handleSectionNameChange(e.target.value?.trim());
                    }
                  }}
                />
              </div>
            </>
          )}
          <div className="panel-title pr-1">
            <h4 className="d-flex align-items-center pl-2 py-2 m-0">
              {t("US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.SECTION_RULES")}
              {` (${sectionRules?.length})`}
            </h4>
            <div className="d-flex align-items-center">
              <$Tooltip
                placement="right"
                title={t("US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.EDIT_RULE")}
              >
                <$Button
                  icon={<EditOutlined />}
                  type="link"
                  size="small"
                  disabled={isSectionLocked()}
                  onClick={onAddNewRule}
                />
              </$Tooltip>
            </div>
          </div>
          <div
            className="p-2 my-1"
            style={{ maxHeight: isXml ? "80%" : "30%", overflowY: "auto" }}
          >
            {Array.isArray(sectionRules) &&
              sectionRules.length > 0 &&
              sectionRules.map(({ rule }: any, index: number) => (
                <div
                  key={rule + index}
                  className={`d-flex py-1 px-2 mb-2 editor-list-item rules numbering ${
                    section.selectedRule == rule ? "active" : ""
                  }`}
                  onClick={() => onSelectRule(rule)}
                  style={{ height: "auto" }}
                >
                  <div
                    style={{ maxWidth: "170px" }}
                    className="align-items-center pl-2"
                  >
                    <Paragraph
                      ellipsis={{
                        rows: 3,
                        tooltip: displayRuleExpression(rule),
                      }}
                      className="text-muted"
                      style={{
                        marginBottom: 0,
                        whiteSpace: "normal",
                        maxWidth: "100%",
                      }}
                    >
                      {displayRuleExpression(rule)}
                    </Paragraph>
                  </div>
                </div>
              ))}
            {Array.isArray(sectionRules) && sectionRules.length == 0 && (
              <div className={`d-flex py-1 px-2 mb-1 editor-list-item active`}>
                <div className="d-flex align-items-center pl-2">
                  <Paragraph
                    className="text-muted"
                    style={{ marginBottom: 0, whiteSpace: "normal" }}
                  >
                    {t(
                      "US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.NO_SECTION_SELECTED"
                    )}
                  </Paragraph>
                </div>
              </div>
            )}
          </div>
          <div className="pb-3">
            <div className="panel-title pr-1">
              <h4 className="d-flex align-items-center pl-2 py-2 m-0">
                {t("US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.PROPERTIES")}
              </h4>
            </div>
            <CommonProperties isXml={isXml} />
            {!isXml && (
              <>
                <$Divider className="my-1" />
                <div className="d-flex pl-2 pt-2">
                  <div className="mr-4">
                    <div className="d-flex text-muted mb-1">
                      <$InputNumber
                        name="width"
                        size={"small"}
                        label={`${t("COMMON.WIDTH")}(cm)`}
                        step="0.01"
                        max={toCentimeters(mazSize.width - Math.abs(x))}
                        stringMode
                        onChange={(value: number) =>
                          handleSizeChange(value, "width")
                        }
                        disabled={isSectionLocked()}
                      />
                    </div>
                    <div className="d-flex">
                      <$InputNumber
                        name="x"
                        size={"small"}
                        label={"X(cm)"}
                        step="0.04"
                        // min={9}
                        max={toCentimeters(
                          mazSize.width - width + margins.left
                        )}
                        stringMode
                        onChange={(value: number) =>
                          handlePositionChange(value - values.x, "x")
                        }
                        onStep={(
                          value: number,
                          info: { offset: number; type: "up" | "down" }
                        ) => {
                          handlePositionChange(
                            info.type === "up" ? 0.04 : -0.04,
                            "x"
                          );
                        }}
                        disabled={isSectionLocked()}
                      />
                    </div>
                  </div>
                  <div>
                    <div className="d-flex mb-1 mr-2">
                      <$InputNumber
                        name="height"
                        size={"small"}
                        label={`${t("COMMON.HEIGHT")}(cm)`}
                        step="0.01"
                        max={toCentimeters(mazSize.height - Math.abs(y))}
                        stringMode
                        onChange={(value: number) =>
                          handleSizeChange(value, "height")
                        }
                        disabled={isSectionLocked()}
                      />
                    </div>
                    <div className="d-flex mr-2">
                      <$InputNumber
                        name="y"
                        size={"small"}
                        label={"Y(cm)"}
                        step="0.04"
                        // min={3.12}
                        max={toCentimeters(
                          mazSize.height - height + margins.top
                        )}
                        stringMode
                        onChange={(value: number) =>
                          handlePositionChange(value - values.y, "y")
                        }
                        onStep={(
                          value: number,
                          info: { offset: number; type: "up" | "down" }
                        ) => {
                          handlePositionChange(
                            info.type === "up" ? 0.04 : -0.04,
                            "y"
                          );
                        }}
                        disabled={isSectionLocked()}
                      />
                    </div>
                  </div>
                </div>
                <div className="d-flex align-items-center mb-2 mt-3 p-2">
                  <span className="mr-5">
                    {t("US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.COLOR")}
                  </span>
                  <$Popover
                    trigger="click"
                    content={
                      <SketchPicker
                        color={section.color}
                        onChange={handleColorChange}
                      />
                    }
                    destroyTooltipOnHide
                    style={{ padding: 0 }}
                  >
                    <$Tag
                      color={section.color}
                      className="flex-grow-1 sketch-picker-btn"
                    />
                  </$Popover>
                  <$Tooltip
                    placement="top"
                    title={t(
                      "US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.CLEAR_BACKGROUND_COLOR"
                    )}
                  >
                    <$Button
                      icon={
                        <BoxIcons
                          type={IconTypes.BOX_ICON}
                          name="reset-color"
                        />
                      }
                      size="middle"
                      onClick={() => {
                        handleColorChange();
                      }}
                    />
                  </$Tooltip>
                </div>
                {Array.isArray(validationErrors) &&
                  validationErrors.length > 0 && (
                    <>
                      <div className="panel-title pr-1">
                        <h4 className="d-flex align-items-center pl-2 py-2 m-0">
                          {t(
                            "US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.VALIDATION_ERRORS"
                          )}
                          {` (${validationErrors?.length})`}
                        </h4>
                      </div>
                      <div
                        className="p-2 my-1"
                        style={{
                          overflowY: "auto",
                        }}
                      >
                        {validationErrors.map(
                          (
                            { error, code }: SectionValidationError,
                            index: number
                          ) => (
                            <div
                              key={code.toString()}
                              className={`d-flex py-1 px-2 mb-2 editor-list-item validation-errors`}
                              style={{ height: "auto" }}
                            >
                              <div
                                style={{ maxWidth: "170px" }}
                                className="align-items-center pl-2"
                              >
                                <Paragraph
                                  ellipsis={{
                                    rows: 3,
                                  }}
                                  className="text-muted"
                                  style={{
                                    marginBottom: 0,
                                    whiteSpace: "normal",
                                    maxWidth: "100%",
                                  }}
                                >
                                  <span>{t(error)}</span>
                                </Paragraph>
                              </div>
                            </div>
                          )
                        )}
                      </div>
                    </>
                  )}
              </>
            )}
          </div>
        </div>
      )}
    </Formik>
  );
};

const mapStateToProps = (state: RootState) => {
  const { templateEditor, templatesHistory } = state;
  const { section, parameters } = templateEditor;
  const { template } = templatesHistory;

  return {
    section,
    parameters,
    template,
  };
};

const { section } = Actions.templateEditor;
const { template } = Actions.templatesHistory;

const mapDispatchToProps = {
  selectSection: section.selectSection,
  updateTemplate: template.update,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(SectionRules);
